import { useMemo, useState, useCallback, memo, useEffect, useRef } from "react";
import { useAuth } from "@/hooks/use-auth";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ModeToggle } from "@/components/ui/mode-toggle";
import { UserTypeSelection } from "./user-type-selection";
import { ProfessionalRegistration } from "./professional-registration";
import { PracticeRegistration } from "./practice-registration";
import { RegistrationReview } from "./registration-review";
import { RegistrationSuccess } from "./registration-success";
import type { RegisterUserType } from "@/domain/core";
import { config } from "@/config/environment";
import {
  type PracticeRegistrationData,
  type ProfessionalRegistrationData,
  type RegisterPracticeRequest,
  type RegisterProfessionalRequest,
} from "@/domain";
import {
  useAuthenticatedPracticesApi,
  useAuthenticatedProfessionalsApi,
} from "@/api";
import { toast } from "sonner";
import { getErrorMessage } from "@/utilities/api-utils";
import { toPositiveMoney } from "@/utilities/money-utils";
import { clearStoredReferralCode } from "@/utilities/referral-utils";

// ============================================================================
// REGISTRATION WIZARD TYPES
// ============================================================================

export interface RegistrationWizardProps {
  onRegistrationComplete?: (userType?: RegisterUserType) => void;
  onCancel?: () => void;
}

// ============================================================================
// REGISTRATION WIZARD COMPONENT
// ============================================================================

export const RegistrationWizard = memo(function RegistrationWizard({
  onRegistrationComplete,
  onCancel,
}: RegistrationWizardProps) {
  const { userInfo, refreshAccessToken } = useAuth();

  // Practice API
  const { useRegisterPracticeMutation } = useAuthenticatedPracticesApi();
  const [
    registerPractice,
    { isLoading: isSubmittingPractice, error: practiceRegistrationError },
  ] = useRegisterPracticeMutation();

  // Professional API
  const { useRegisterProfessionalMutation } =
    useAuthenticatedProfessionalsApi();
  const [
    registerProfessional,
    {
      isLoading: isSubmittingProfessional,
      error: professionalRegistrationError,
    },
  ] = useRegisterProfessionalMutation();

  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Loading and Error States
  const isSubmitting = useMemo(
    () =>
      isSubmittingPractice || isSubmittingProfessional || isRefreshingToken,
    [isSubmittingPractice, isSubmittingProfessional, isRefreshingToken]
  );

  // Registration Error
  const registrationError = useMemo(
    () => practiceRegistrationError || professionalRegistrationError,
    [practiceRegistrationError, professionalRegistrationError]
  );

  // Local State
  const [currentStep, setCurrentStep] = useState(1);

  // Registration Data State
  const [selectedUserType, setSelectedUserType] =
    useState<RegisterUserType>("professional");
  const registrationDataPracticeRef = useRef<PracticeRegistrationData>({
    userId: userInfo && userInfo.userId ? userInfo.userId : "",
    email: userInfo && userInfo.email ? userInfo.email : "",
    firstName: userInfo && userInfo.firstName ? userInfo.firstName : "",
    namePrefix: "Dr.",
    middleName:
      userInfo && userInfo.middleName ? userInfo.middleName : undefined,
    lastName: userInfo && userInfo.lastName ? userInfo.lastName : "",
    phone: "",
    designation: "",
    practiceName: "",
    taxId: "",
    licenseNumber: "",
    branchName: "",
    addressCountry: "Canada",
  });

  const registrationDataProfessionalRef = useRef<ProfessionalRegistrationData>({
    userId: userInfo && userInfo.userId ? userInfo.userId : "",
    email: userInfo && userInfo.email ? userInfo.email : "",
    firstName: userInfo && userInfo.firstName ? userInfo.firstName : "",
    namePrefix: "Dr.",
    middleName: userInfo && userInfo.middleName ? userInfo.middleName : "",
    lastName: userInfo && userInfo.lastName ? userInfo.lastName : "",
    phone: "",
    designation: "",
    yearsOfExperience: "",
    skills: [],
    specialties: [],
    addressCountry: "Canada",
  });

  useEffect(() => {
    if (userInfo?.userId && userInfo?.email) {
      registrationDataPracticeRef.current = {
        ...registrationDataPracticeRef.current,
        userId: userInfo.userId,
        email: userInfo.email,
      };
      registrationDataProfessionalRef.current = {
        ...registrationDataProfessionalRef.current,
        userId: userInfo.userId,
        email: userInfo.email,
      };
    }
  }, [userInfo?.userId, userInfo?.email]);

  // Handlers

  const handleUserTypeSelection = useCallback((userType: RegisterUserType) => {
    setSelectedUserType(userType);
    setCurrentStep(2);
  }, []);

  const handleProfessionalData = useCallback(
    (data: ProfessionalRegistrationData) => {
      registrationDataProfessionalRef.current = {
        ...registrationDataProfessionalRef.current,
        ...data,
      };
      setCurrentStep(3);
    },
    []
  );

  const handlePracticeData = useCallback((data: PracticeRegistrationData) => {
    registrationDataPracticeRef.current = {
      ...registrationDataPracticeRef.current,
      ...data,
    };
    setCurrentStep(3);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!userInfo || !userInfo.userId || !userInfo.email) {
      toast.error("User ID or email not available. Please sign in again.");
      return;
    }

    try {
      setIsRefreshingToken(true);

      // Practice
      if (selectedUserType === "practice") {
        // Prepare registration request data for practice
        const registerPracticeRequest: RegisterPracticeRequest = {
          // User
          userId: userInfo.userId,
          email: userInfo.email,
          namePrefix: registrationDataPracticeRef.current.namePrefix,
          firstName: registrationDataPracticeRef.current.firstName,
          middleName:
            registrationDataPracticeRef.current.middleName?.trim() || undefined,
          lastName: registrationDataPracticeRef.current.lastName,
          phone: registrationDataPracticeRef.current.phone,
          designation: registrationDataPracticeRef.current.designation,
          // Practice
          practiceName: registrationDataPracticeRef.current.practiceName,
          website: registrationDataPracticeRef.current.website,
          description: registrationDataPracticeRef.current.description,
          taxId: registrationDataPracticeRef.current.taxId,
          licenseNumber: registrationDataPracticeRef.current.licenseNumber,
          // Branch
          branchName: registrationDataPracticeRef.current.branchName,
          // Address
          addressCountry: registrationDataPracticeRef.current.addressCountry,
          addressState: registrationDataPracticeRef.current.addressState,
          addressCity: registrationDataPracticeRef.current.addressCity,
          addressStreet: registrationDataPracticeRef.current.addressStreet,
          addressPostalCode:
            registrationDataPracticeRef.current.addressPostalCode,
          addressLongitude:
            registrationDataPracticeRef.current.addressLongitude,
          addressLatitude: registrationDataPracticeRef.current.addressLatitude,
        };

        // Call the registration API using RTK Query mutation
        await registerPractice(registerPracticeRequest).unwrap();
      }
      // Professional
      else if (selectedUserType === "professional") {
        // Prepare registration request data for professional
        const registerProfessionalRequest: RegisterProfessionalRequest = {
          // User
          userId: userInfo.userId,
          email: userInfo.email,
          namePrefix: registrationDataProfessionalRef.current.namePrefix,
          firstName: registrationDataProfessionalRef.current.firstName,
          middleName:
            registrationDataProfessionalRef.current.middleName?.trim() ||
            undefined,
          lastName: registrationDataProfessionalRef.current.lastName,
          phone: registrationDataProfessionalRef.current.phone,
          designation: registrationDataProfessionalRef.current.designation,
          // Professional
          bio: registrationDataProfessionalRef.current.bio,
          hourlyRateMin: toPositiveMoney(
            registrationDataProfessionalRef.current.hourlyRateMin
          ),
          hourlyRateMax: toPositiveMoney(
            registrationDataProfessionalRef.current.hourlyRateMax
          ),
          maxTravelDistance:
            registrationDataProfessionalRef.current.maxTravelDistance,
          licenseNumber: registrationDataProfessionalRef.current.licenseNumber,
          yearsOfExperience:
            registrationDataProfessionalRef.current.yearsOfExperience,
          specialties: registrationDataProfessionalRef.current.specialties,
          skills: registrationDataProfessionalRef.current.skills,
          // Address
          addressCountry:
            registrationDataProfessionalRef.current.addressCountry,
          addressState: registrationDataProfessionalRef.current.addressState,
          addressCity: registrationDataProfessionalRef.current.addressCity,
          addressStreet: registrationDataProfessionalRef.current.addressStreet,
          addressPostalCode:
            registrationDataProfessionalRef.current.addressPostalCode,
          addressLongitude:
            registrationDataProfessionalRef.current.addressLongitude,
          addressLatitude:
            registrationDataProfessionalRef.current.addressLatitude,
          // Referral
          referralCode:
            registrationDataProfessionalRef.current.referralCode?.trim() ||
            undefined,
        };

        // Call the registration API using RTK Query mutation
        await registerProfessional(
          registerProfessionalRequest
        ).unwrap();

        // Redeemed — the parked code must not attach itself to a later sign-up.
        clearStoredReferralCode();
      }

      // Replace the sign-in AT immediately. It predates Entra group
      // assignment and would 403 on RequireAnyRole dashboard calls.
      const tokenResult = await refreshAccessToken({ forceRefresh: true });
      if (!tokenResult.success && config.debug.enabled) {
        console.warn(
          "Immediate post-registration token refresh failed:",
          tokenResult.error
        );
      }

      // Success Step
      setCurrentStep(4);

      // Don't call completion callback immediately - let the success component handle it
      // The success component will call onComplete after the post-registration flow is done
    } catch (error) {
      toast.error(getErrorMessage(error) || "Registration failed. Please try again.");
      if (config.debug.enabled) {
        console.error("Registration error:", error);
      }
    } finally {
      setIsRefreshingToken(false);
    }
  }, [
    userInfo,
    selectedUserType,
    registerPractice,
    registerProfessional,
    refreshAccessToken
  ]);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const renderStep = useCallback(() => {
    if (!userInfo) return <></>;
    switch (currentStep) {
      case 1:
        return (
          <UserTypeSelection
            onSelect={handleUserTypeSelection}
            onCancel={handleCancel}
            userInfo={userInfo}
          />
        );

      case 2:
        return selectedUserType === "professional" ? (
          <ProfessionalRegistration
            data={registrationDataProfessionalRef.current}
            userInfo={userInfo}
            onSubmit={handleProfessionalData}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        ) : (
          <PracticeRegistration
            data={registrationDataPracticeRef.current}
            userInfo={userInfo}
            onSubmit={handlePracticeData}
            onBack={handleBack}
            onCancel={handleCancel}
          />
        );

      case 3:
        return (
          <RegistrationReview
            selectedUserType={selectedUserType}
            registrationDataProfessional={
              registrationDataProfessionalRef.current
            }
            registrationDataPractice={registrationDataPracticeRef.current}
            userInfo={userInfo}
            onSubmit={handleSubmit}
            onBack={handleBack}
            onCancel={handleCancel}
            isSubmitting={isSubmitting}
            error={registrationError}
          />
        );

      case 4:
        return (
          <RegistrationSuccess
            userType={selectedUserType}
            onComplete={() => onRegistrationComplete?.(selectedUserType)}
          />
        );

      default:
        return null;
    }
  }, [
    currentStep,
    handleUserTypeSelection,
    handleCancel,
    userInfo,
    selectedUserType,
    handleProfessionalData,
    handleBack,
    handlePracticeData,
    handleSubmit,
    isSubmitting,
    registrationError,
    onRegistrationComplete,
  ]);

  // Render
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 z-10">
        <ModeToggle />
      </div>

      <Card className="w-full max-w-4xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">
            Welcome to Tempii
          </CardTitle>
          <CardDescription>
            Complete your registration to get started
          </CardDescription>

          {/* Progress indicator */}
          <div className="flex justify-center mt-4">
            <div className="flex space-x-2">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`w-3 h-3 rounded-full ${step <= currentStep ? "bg-primary" : "bg-muted"
                    }`}
                />
              ))}
            </div>
          </div>
        </CardHeader>

        <CardContent>{renderStep()}</CardContent>
      </Card>
    </div>
  );
});
